/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2014-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.compositeViewModule')
  .controller('CompositeViewController', [
    '$',
    '$location',
    '$q',
    '$rootScope',
    '$scope',
    '$state',
    '$timeout',
    '$translate',
    '$window',
    'Restangular',
    'ImageCropperDialogService',
    'AeroDialogService',
    'AnalyticsService',
    'AssignmentService',
    'BulbAssetLibraryService',
    'BulbAssetLibrarySnapshotPdfService',
    'BulbAuthService',
    'BulbAvatarService',
    'BulbBusyService',
    'BulbCoverImageService',
    'BulbDeletedUnitService',
    'BulbErrorService',
    'BulbGroupService',
    'BulbModalService',
    'BulbInappropriateContentService',
    'BulbPageService',
    'BulbPresentationService',
    'BulbRecommendService',
    'BulbShareService',
    'BulbUnitService',
    'BulbUseTemplateModalService',
    'BulbUtilitiesService',
    'ErrorService',
    'RecordEventService',
    'ResumeService',
    'SerialSaveService',
    'UnitStateStore',
    'AUTH',
    'CONFIG',
    'USE_TEMPLATE_TIMEOUT',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    CompositeViewController,
  ]);

/**
 * AngularJS controller for composite views.
 */
function CompositeViewController(
  $,
  $location,
  $q,
  $rootScope,
  $scope,
  $state,
  $timeout,
  $translate,
  $window,
  Restangular,
  ImageCropperDialogService,
  AeroDialogService,
  AnalyticsService,
  AssignmentService,
  BulbAssetLibraryService,
  BulbAssetLibrarySnapshotPdfService,
  BulbAuthService,
  BulbAvatarService,
  BulbBusyService,
  BulbCoverImageService,
  BulbDeletedUnitService,
  BulbErrorService,
  BulbGroupService,
  BulbModalService,
  BulbInappropriateContentService,
  BulbPageService,
  BulbPresentationService,
  BulbRecommendService,
  BulbShareService,
  BulbUnitService,
  BulbUseTemplateModalService,
  BulbUtilitiesService,
  ErrorService,
  RecordEventService,
  ResumeService,
  SerialSaveService,
  UnitStateStore,
  AUTH,
  CONFIG,
  USE_TEMPLATE_TIMEOUT,
) {
  const that = this;
  this.isUserCoverImage = this.compositeUnit.isUserCoverImage;

  /**
   * Prevent ADMIN users from completing on-boarding steps for new users when using 'sign in as' to diagnose problems.
   */
  if (BulbAuthService.currentUser.hasRole(AUTH.ROLES.SWITCHED_USER)) {
    that.switchedUser = true;
  }

  /**
   * If the unit is a group, add the start and end date variables to be displayed in the template
   * for group effective dates
   */
  if (this.compositeUnit.unitType === 'GroupUnit' && this.compositeUnit.apiCreated) {
    this.startDate = this.compositeUnit.startDate ? BulbUtilitiesService
      .convertDateWithTranslatedMonth(this.compositeUnit.startDate) : '';
    this.endDate = this.compositeUnit.endDate ? BulbUtilitiesService
      .convertDateWithTranslatedMonth(this.compositeUnit.endDate) : '';
    this.isOverridden = this.compositeUnit.overrideAutoDates;
    this.effectiveDatesIcon = this.isOverridden ? 'exclamation-triangle' : 'calendar-alt';
  }
  /**
   * Check if current account is the owner of the unit
   */
  this.isOwner = BulbAuthService.currentUser.username === this.compositeUnit.ownerUsername;

  this.myArchiveCollectionTitle = $translate.instant('ArchiveCompositeUnit');
  this.currentUserPrefs = BulbAuthService.currentUser.prefs;
  this.currentUser = BulbAuthService.currentUser;

  /**
   * Author Resume object.
   */
  this.resume = null;

  /**
   * Set the value to true if resume tab is selected else false.
   */
  this.isResumeTabSelected = false;
  this.profileUsername = $state.params.username;
  this.compositeViewOwner = $scope.authorCtrl ? $scope.authorCtrl.authorAccount : null;
  this.allowed = BulbAuthService.currentUser.allowed;
  this.assignmentCanAssign = BulbAuthService.currentUser.features.AssignmentAccountFeature.canAssign;

  /**
   * Determines the composite view type from the given compositeUnit
   * @param compositeUnit
   * @returns String a string constant representing this composite view's type
   */
  function getViewType(compositeUnit) {
    switch (compositeUnit.unitType) {
      case 'CompositeUnit':
        return 'collection';
      case 'AssignmentCompositeUnit':
        return 'assignment';
      case 'ArchiveCompositeUnit':
        return 'archive';
      case 'GroupUnit':
        return 'group';
      case 'UserUnit':
        return 'author';
      default:
        return compositeUnit.unitType;
    }
  }

  this.viewType = getViewType(this.compositeUnit);

  // Fetching the unit id from the composite unit, if composite unit is of group type then use the groupUnitId.
  this.compositeUnitId =  that.viewType === 'group' ? that.compositeUnit.groupUnitId : that.compositeUnit.id;

  // eslint-disable-next-line no-nested-ternary
  const compositeHeaderType = (getViewType(this.compositeUnit) !== 'author'
    && getViewType(this.compositeUnit) !== 'group')
    ? 'composite_unit_name_placeholder'
    : (getViewType(this.compositeUnit) === 'author')
      ? 'composite_unit_profile_name_placeholder' : 'composite_unit_group_name_placeholder';

  this.compositeHeaderTitle = $translate.instant(compositeHeaderType);

  this.edit = null;

  this.isCoverImageUploadActive = false;

  this.isAvatarUploadActive = false;
  this.avatarImage = null;

  BulbUnitService.fetchParent();

  this.canCopyTemplate = this.compositeUnit.template;
  this.canUseTemplateFeature = BulbAuthService.currentUser.isAuthenticated()
    && BulbAuthService.currentUser.features.TemplateFeature.enabled;

  this.isTemplate = this.compositeUnit.template;

  // eslint-disable-next-line no-param-reassign
  BulbDeletedUnitService.isRecentlyDeletedSelected = false;

  /**
   * Controls whether the default composite header (containing cover image, title, and text content)
   * or the imageEditor (for cropping or deleting the cover image) is shown.
   * Values:
   *     'default': show the default composite header.
   *     'editor': show the image editor.
   * @type {string}
   */
  this.mode = 'default';

  this.canEdit = this.compositeUnit.unitType !== 'AssignmentCompositeUnit' && BulbAuthService.currentUser.hasPermission(this.compositeUnit, AUTH.PERMISSIONS.UPDATE);

  this.canShowMobileLaunchToast = this.viewType === 'author' && this.canEdit;

  // Set up the avatar image, if author or group view.
  if (this.viewType === 'author' || this.viewType === 'group') {
    this.avatarImage = this.compositeUnit.circularImage;
  }

  // Set up the open graph image for this CompositeUnit:
  // NOTE : This doesn't need to be updated dynamically, as the openGraph image is only accessed when viewing the
  // readonly version of a page
  this.openGraphImage = this.avatarImage || this.compositeUnit.coverImage;

  $scope.$watchCollection(() => BulbAuthService.currentUser.prefs, () => {
    that.currentUserPrefs = BulbAuthService.currentUser.prefs;
  });

  /**
   * Show the resume share link dialogue only if account is not FREE.
   * For FREE accounts,  A dialogue showing resume is a premium feature will be displayed.
   */
  function showShareResumeModal() {
    if (BulbAuthService.currentUser.orgSuspended) {
      AeroDialogService.showOrgSuspended();
    } else if (BulbAuthService.currentUser.features.ResumeFeature.sharingEnabled) {
      AeroDialogService.showShareLinkDialog(BulbAuthService.currentUser, true);
    } else {
      AeroDialogService.showPlanUpgradeDialog();
    }
  }

  /**
   * Show the portfolio share link dialogue only if account is not FREE.
   * For FREE accounts, A dialogue showing portfolio sharing is a premium feature will be displayed.
   */
  function showSharePortfolioModal() {
    if (BulbAuthService.currentUser.featureSet !== 'BASIC') {
      AeroDialogService.showShareLinkDialog(BulbAuthService.currentUser, true, false);
    } else {
      AeroDialogService.showPlanUpgradeDialog();
    }
  }

  /**
   *  Watcher to update if the resume tab is selected.
   */
  $scope.$watch(() => ResumeService.isResumeTabSelected, () => {
    that.fetchResumeDetails();
    // This function is defined below, along with other Angular-related functions.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    updatePageStateForAngular();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    updateMenuItemLabels();
  });

  /**
   *  Watcher to update if the resume Contact details are updated.
   */
  $scope.$watch(() => ResumeService.isResumeContactUpdated, () => {
    if (ResumeService.isResumeContactUpdated) {
      that.fetchResumeDetails();
      // eslint-disable-next-line no-param-reassign
      ResumeService.isResumeContactUpdated = false;
    }
  });

  /**
   * Fetch the resume details for the user.
   */
  this.fetchResumeDetails = function fetchResumeDetails() {
    that.isResumeTabSelected = ResumeService.isResumeTabSelected;
    if ($scope.authorCtrl && $scope.authorCtrl.authorAccount.resumeId && $scope.authorCtrl.showResume) {
      if (that.isResumeTabSelected) {
        ResumeService.fetchResume($scope.authorCtrl.authorAccount.resumeId).then((resume) => {
          that.resume = resume;
        });
      }
    }
  };

  this.autoSaveCallback = function autoSaveCallback(response) {
    const savedUnit = response;
    switch (that.viewType) {
      case 'collection':
        // Update the address bar to reflect any path changes without reloading the state.
        // See $location decorator in app.js for skipReload().
        $location.skipReload().path(`/u/${savedUnit.path}`).replace();

        // Update the browser title:
        // eslint-disable-next-line no-param-reassign
        $rootScope.$title = savedUnit.name;

        // FUTURE: some more general way of updating interested components?
        BulbRecommendService.updateRecommend(savedUnit);
        break;

      case 'group':
        // FUTURE: some more general way of updating interested components?
        BulbGroupService.updateGroup(response);
        break;

      default:
        break;
    }
    // DO LATER : we should replace the current compositeUnit with the one returned from the save, so that any
    // other changes that happened on the server are displayed correctly.  Tried just assigning the savedUnit to
    // this.compositeUnit, but it didn't update the display.  Perhaps something like an angular.copy would work,
    // or perhaps there is some code that doesn't like simply changing the compositeUnit dynamically..
  };

  this.getReadAccessSubjectIds = function getReadAccessSubjectIds() {
    return that.selectedVisibilityChoices
      .filter((selection) => !selection.inheritance)
      .map((selection) => selection.subjectId);
  };

  this.autoSaveOptions = {
    restId: this.compositeUnit.id,
    callback: this.autoSaveCallback,
    restCollection: this.viewType === 'group' ? 'groups' : 'units',
  };

  // The visibility select control has the same options as the other controls, but with the addition of a getter
  // to retrieve the value in a format appropriate for the save call.
  this.selectAutoSaveOptions = angular.copy(this.autoSaveOptions);
  this.selectAutoSaveOptions.getter = this.getReadAccessSubjectIds;

  // TODO: This might be dead code. Looks like it was moved to devtools
  //       If you see this, try to remove it. Should be safe to remove.
  if (process.env.BOOTSTRAP_ENABLED) {
    this.subscribeToBulb = function subscribeToBulb() {
      Restangular.one('bootstrap', 'subscribe').get().then(() => {
        $window.location.reload();
      }, (error) => {
        BulbErrorService.error(error);
      });
    };

    this.unsubscribeFromBulb = function unsubscribeFromBulb() {
      Restangular.one('bootstrap', 'unsubscribe').get().then(() => {
        $window.location.reload();
      }, (error) => {
        BulbErrorService.error(error);
      });
    };

    this.unacceptTerms = function unacceptTerms() {
      Restangular.one('accounts', BulbAuthService.currentUser.id).patch({
        acceptedLatestTerms: false,
      }).catch((error) => {
        BulbErrorService.error(error);
      });
    };
  }

  /**
   * Edit existing cover image. Get the original image and current center values from the server.
   * Display the imageEditor with this image, centered as specified,
   * with Cancel and Delete controls and a Save button.
   */
  this.editCoverImage = function editCoverImage() {
    BulbBusyService.showBusyDuring(
      // get cover image asset from server
      Restangular.one('images', that.compositeUnit.coverImage.id).customGET('originalimage/coverImage')
        .then((coverImage) => {
          that.edit = {
            isNew: !that.compositeUnit.coverImage,
            asset: {
              id: coverImage.id,
              assetDefinitionsKey: 'coverImage',
              url: coverImage.url,
              width: coverImage.width,
              height: coverImage.height,
              center: {
                X: coverImage.centerX,
                Y: coverImage.centerY,
              },
            },
            cropType: 'CROP_COVER_IMAGE',
          };
          that.mode = 'editor';
          that.isCoverImageUploadActive = false;
        }, (error) => {
          BulbErrorService.error('Cannot get original image for cover image', error);
        }),
    );
  };

  /**
   * This function is used to delete the cover image, it acts as a first step allowing the modal to show to the user
   * and when the user approves the deletion, it will delete the cover image.
   */
  this.deleteCoverImage = function deleteCoverImage() {
    $translate('cover_image_type').then((coverImageText) => {
      const modalTitle = $translate.instant('modal_delete_confirm_title', { type: coverImageText });
      $translate('modal_delete_confirm_body', { type: coverImageText }).then((bodyText) => {
        BulbModalService.show({
          titleCode: modalTitle,
          bodyText,
          fontAwesomeCode: 'far fa-trash-alt',
          windowClass: 'bulb-aero-modal-body bulb-aero-modal-buttons bulb-aero-modal-buttons-danger',
          okButtonCode: 'button_delete',
        }).then(() => {
          // user confirmed the confirm delete modal
          BulbBusyService.showBusyDuring(
            that.onCoverImageDelete(),
          );
        });
      });
    });
  };

  /**
   * Configured upload on-file-uploaded handler. See upload-controller.js.
   * When image file has been uploaded, open an imageEditor,
   * with crop type CROP_COVER_IMAGE, for the asset uploaded.
   * @param entry The server response from the upload, containing asset information.
   *
   * The edit object has the following properties:
   * asset:
   * id: uploaded asset's id
   * assetDefinitionsKey: uploaded asset's assetDefinitionsKey,
   * url: uploaded asset's URL,
   * width: uploaded asset's width,
   * height: uploaded asset's height,
   * center: uploaded asset's already-specified center (optional, used only for edit image),
   * cropType: Specifier for the type of crop to be performed, interpreted by the imageEditor directive.
   * @returns a resolved promise
   */
  this.onCoverImageUploaded = function onCoverImageUploaded(entry) {
    const deferred = $q.defer();

    if (entry) {
      const setting = CONFIG.coverimage;

      if (entry.image.width < setting.minwidth || entry.image.height < setting.minheight) {
        // We reject it by default, to prevent the image from being allowed through to the editor.
        deferred.reject();
        // Then we show the modal, and allow the user to select a new one, or use the current one blurry.
        BulbCoverImageService.calculateCoverImageSize(entry, setting, that, 'compositeView');
      } else {
        that.mode = 'editor';
        that.isCoverImageUploadActive = false;
        that.edit = {
          isNew: !that.compositeUnit.coverImage,
          asset: {
            id: entry.image.id,
            assetDefinitionsKey: 'coverImage',
            url: entry.image.url,
            width: entry.image.width,
            height: entry.image.height,
          },
          cropType: 'CROP_COVER_IMAGE',
        };
        deferred.resolve();
      }
    } else {
      deferred.reject();
    }
    const bulbMainDiv = $('.bulb-main');
    const bulbMainParentDiv = bulbMainDiv.parent();
    if (bulbMainParentDiv.hasClass('bulb-aria-hide')) {
      bulbMainParentDiv.removeClass('bulb-aria-hide');
    }
    return deferred.promise;
  };

  /**
   * Configured imageEditor on-edit-complete handler. See imageEditor-directive.js.
   * When user clicks Save in the imageEditor, process the edit data.
   * If data is set: send new edit data to server, then update composite unit and display the new cover image,
   * and resolve the promise with true.
   * If not, edit was canceled: do nothing, except return to default state, and resolve the promise with true.
   * @param data The image URL and crop information generated by the edit.
   * @returns a promise, which resolves to true or false.
   */
  this.onCoverImageEditComplete = function onCoverImageEditComplete(data) {
    const deferred = $q.defer();
    this.edit = null;
    if (data) {
      const uploadAsset = {
        assetId: data.asset.id,
        assetDefinitionsKey: data.asset.assetDefinitionsKey,
        centerX: data.center.X,
        centerY: data.center.Y,
      };

      SerialSaveService.save({
        save: () => Restangular.one('units', that.compositeUnitId).customPOST(uploadAsset, 'coverimage')
          .then((compositeUnit) => {
            that.compositeUnit = compositeUnit;
            that.mode = 'default';
            that.isCoverImageUploadActive = false;
            // FUTURE: some more general way of updating interested components?
            BulbRecommendService.updateRecommend(compositeUnit);

            // Update share status to reflect that the unit has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            deferred.resolve(true);
          }),
      }).catch((error) => {
        const errorMessage = BulbErrorService.error('Cannot update unit cover image', error);
        deferred.reject(errorMessage);
      });
    } else {
      this.mode = 'default';
      this.isCoverImageUploadActive = false;
      deferred.resolve(true);
    }
    return deferred.promise;
  };

  /**
   * Configured imageEditor on-delete handler. See imageEditor-directive.js.
   * Delete the cover image on the server, and clear the visible cover image,
   * and resolve the promise with true.
   * On error, reject the promise.
   * @returns a promise.
   */
  this.onCoverImageDelete = function onCoverImageDelete() {
    // Delete the cover image
    return SerialSaveService.save({
      save: () => Restangular.one('units', that.compositeUnitId).customDELETE('coverimage')
        .then((compositeUnit) => {
          that.compositeUnit = compositeUnit;
          that.mode = 'default';
          that.isCoverImageUploadActive = false;
          // FUTURE: some more general way of updating interested components?
          BulbRecommendService.updateRecommend(compositeUnit);

          // Update share status to reflect that the unit has been edited:
          const ShareTransition = UnitStateStore.getShareTransitionEnum();
          UnitStateStore.transitionShareStatus(ShareTransition.EDIT);
        }),
    });
  };

  this.onAvatarUploaded = function onAvatarUploaded(entry) {
    that.isAvatarUploadActive = false;
    const deferred = $q.defer();
    if (entry) {
      const targetImage = entry.croppedImage || entry.image;

      const { width } = targetImage;
      const { height } = targetImage;
      let cropX; let cropY; let cropWidth; let
        cropHeight;
      // largest possible centered square crop
      if (width > height) {
        cropX = (width - height) / 2.0;
        cropY = 0;
        cropWidth = height;
        cropHeight = height;
      } else {
        cropX = 0;
        cropY = (height - width) / 2.0;
        cropWidth = width;
        cropHeight = width;
      }

      const uploadAsset = {
        assetId: targetImage.id,
        assetDefinitionsKey: 'circularImage',
        cropX,
        cropY,
        cropWidth,
        cropHeight,
      };

      Restangular.one((that.viewType === 'group' ? 'groups' : 'units'), that.compositeUnit.id)
        .customPOST(uploadAsset, 'circularimage').then((compositeUnit) => {
          that.compositeUnit = compositeUnit;
          if (that.viewType === 'author') {
            that.avatarImage = that.compositeUnit.circularImage;
            ResumeService.updateProfileImage.next(compositeUnit.circularImage);
          } else if (that.viewType === 'group') {
            that.avatarImage = that.compositeUnit.circularImage;
            BulbGroupService.updateGroup(that.compositeUnit);
          }
          deferred.resolve(true);
        }, (error) => {
          const reason = BulbErrorService.error('Cannot set avatar image', error);
          deferred.reject(reason);
        }).then(() => {
          that.editAvatar();
        });
    } else {
      deferred.resolve(false);
    }

    return deferred.promise;
  };

  this.deleteAvatar = function deleteAvatar() {
    const translatedType = $translate.instant('avatar_type');
    const typeToLowercase = translatedType.toLowerCase();
    AeroDialogService.showDialog({
      icon: 'far fa-trash-alt',
      iconClass: 'aero-alert',
      title: $translate.instant('modal_delete_confirm_title', { type: typeToLowercase }),
      detail: $translate.instant('modal_delete_confirm_body', { type: typeToLowercase }),
      primaryLabel: $translate.instant('button_delete'),
      primaryStyle: AeroDialogService.getButtonStyleEnum().NEGATIVE_ACTION,
      onPrimary: () => {
        deleteProfileAvatar();
      },
    });
  };

  // Edit the user's avatar image
  this.editAvatar = function editAvatar() {
    // Always fetch the original avatar image from the server so that we don't keep losing data on crop:
    BulbBusyService.showBusyDuring(
      Restangular.one('images', that.compositeUnit.circularImage.id)
        .customGET('originalimage/circularImage')
        .then((circularImage) => {
          BulbAssetLibraryService.dialog.image.showEditProfileImageModal(circularImage, (response) => {
            if (response) {
              const uploadAsset = {
                assetId: response.imageAsset.id,
                assetDefinitionsKey: 'circularImage',
                cropX: response.croppedData.x,
                cropY: response.croppedData.y,
                cropWidth: response.croppedData.width,
                cropHeight: response.croppedData.height,
                rotationDegrees: response.croppedData.rotate,
              };
              BulbBusyService.showBusyDuring(
                Restangular.one((that.viewType === 'group' ? 'groups' : 'units'), that.compositeUnit.id)
                  .customPOST(uploadAsset, 'circularimage').then((compositeUnit) => {
                    that.compositeUnit = compositeUnit;
                    that.avatarImage = that.compositeUnit.circularImage;
                    ResumeService.updateProfileImage.next(compositeUnit.circularImage);
                  }, (error) => {
                  // an error occurred when updating the avatar image
                    BulbErrorService.error('Cannot update avatar', error);
                  }).then(() => {
                    BulbAssetLibraryService.dialog.dismissAllModals();
                  }),
              );
            }
          });
        }),
    );
  };

  /**
   * Initiates the create-collection flow.
   *
   * Currently, the only allowed parent for a collection is the author page.
   */
  const createCollection = function createCollection() {
    if (that.viewType === 'collection') {
      if (BulbAuthService.currentUser.features.SubCollectionFeature.enabled) {
        $state.go('createSubCollection', { unitPath: that.compositeUnit.path });
      } else {
        BulbModalService.show({
          titleCode: 'create_collection_limit_reached_title',
          bodyCode: (
            BulbAuthService.currentUser.bulbSchoolDirectMemberships > 0
              ? 'create_collection_limit_reached_content_bulbSchoolDirect'
              : 'create_collection_limit_reached_content'),
          showOkButton: false,
          showCancelButton: false,
        });
      }
    } else {
      $state.go('createCollection');
    }
  };

  /**
   * Indicates whether the current user has permission to create a child page with this compositeUnit as the
   * parent.
   */
  this.canCreatePage = function canCreatePage() {
    return ((this.viewType === 'author' || this.viewType === 'collection')
      && BulbAuthService.currentUser.hasPermission(this.compositeUnit, AUTH.PERMISSIONS.UPDATE));
  };

  /**
   * Method that is called from deleteResumeUnitModal and deleteUnitModal. To delete a page you should call delete()
   */
  function deleteUnitWithoutModal() {
    SerialSaveService.save({
      showBusy: true,
      save: () => Restangular.one('units', that.compositeUnit.id).remove().then(() => {
        const { parentUnit } = BulbUnitService;
        if (parentUnit && parentUnit.unitType === 'CompositeUnit') {
          $state.go('unit', { unitPath: parentUnit.path });
        } else {
          $state.go('author', { username: BulbAuthService.currentUser.username });
        }
      }),
    });
  }

  /**
   * Method that is called to delete avatar.
   */
  function deleteProfileAvatar() {
    // user confirmed the confirm delete modal
    BulbBusyService.showBusyDuring(
      // Delete the circular image
      Restangular.one((that.viewType === 'group' ? 'groups' : 'units'), that.compositeUnit.id)
        .customDELETE('circularimage').then((compositeUnit) => {
          that.compositeUnit = compositeUnit;
          that.avatarImage = null;
          ResumeService.updateProfileImage.next(compositeUnit.circularImage);
          if (that.viewType === 'group') {
            // FUTURE: some more general way of updating interested components?
            BulbGroupService.updateGroup(that.compositeUnit);
          }
        }, (error) => {
          BulbErrorService.error('Cannot delete avatar', error);
        }),
    );
  }

  /**
   * Shows the delete-unit modal for units attached to resume, private function.
   * To delete a page you should call delete()
   */
  function deleteResumeCollectionModal() {
    BulbModalService.show({
      titleCode: 'soft_delete_resume_title',
      bodyCode: 'soft_delete_resume_body',
      okButtonCode: 'button_delete',
      cancelButtonCode: 'button_cancel',
      fontAwesomeCode: 'far fa-trash-alt',
      windowClass: 'updated-modal-design bulb-aero-modal bulb-delete-resume-confirmation',
      windowId: 'bulb-delete-resume-confirmation-modal',
    }).then(() => {
      deleteUnitWithoutModal();
    });
  }

  /**
   * Shows the standard delete unit modal for collections, private function.
   * To delete a page you should call delete()
   */
  function deleteCollectionModal() {
    const translatedType = $translate.instant('type_composite_unit');
    const typeToLowercase = translatedType.toLowerCase();

    AeroDialogService.showDialog({
      icon: 'far fa-trash-alt',
      iconClass: 'aero-alert',
      title: $translate.instant('modal_delete_confirm_title', { type: typeToLowercase }),
      detail: $translate.instant('soft_delete_modal_delete_type_confirm_page', { type: typeToLowercase }),
      primaryLabel: $translate.instant('button_delete'),
      primaryStyle: AeroDialogService.getButtonStyleEnum().NEGATIVE_ACTION,
      onPrimary: () => {
        deleteUnitWithoutModal();
      },
    });
  }

  /**
  * Toggle whether the current user has hidden the title for the current collection.
  */
  const toggleTitleHidden = function toggleTitleHidden($event) {
    $event.stopPropagation();
    $event.preventDefault();

    that.compositeUnit.titleHidden = !that.compositeUnit.titleHidden;
    const payload = {
      titleHidden: that.compositeUnit.titleHidden,
    };

    SerialSaveService.save({
      save: () => Restangular.one('units', that.autoSaveOptions.restId).customPOST(payload, 'update'),
    });
  }

  /**
   * Return text for re-sync classes from Google Classroom.
   * @returns {string}
   */
  this.reSyncCode = function reSyncCode() {
    if (Foundation.utils.is_small_only()) { // jshint ignore:line
      return '';
    }
    return 'import_class_from_classroom_resync';
  };

  /**
   * Handles copying the current composite unit as a template.
   * Shows a loading modal during the process, and depending on the
   * outcome, displays either a success or error modal.
   *
   * @param {Event} event - Triggering event
   * @param {boolean} copyContents - Whether to include contents in the copy
   */
  this.handleCopyTemplate = function handleCopyTemplate(event, copyContents) {
    const ref = BulbUseTemplateModalService.useTemplateCopyingModal();

    const useTemplateCopyTimeout = $timeout(() => {
      ref.close();
      BulbUseTemplateModalService.showErrorModal(event);
    }, USE_TEMPLATE_TIMEOUT);

    SerialSaveService.save({
      save: () => Restangular.one('units', that.compositeUnit.id)
        .customPOST({}, 'copyTemplate', { isArchive: false, copyContents })
        .then(() => {
          $timeout.cancel(useTemplateCopyTimeout);
          ref.destroy();
          BulbUseTemplateModalService.useTemplateCopiedModal(event, that.compositeUnit.id, 'Collection');
        }),
    }).catch((error) => {
      $timeout.cancel(useTemplateCopyTimeout);
      ref.close();
      if (error && error.data.status === 'INTERNAL_SERVER_ERROR') {
        BulbUseTemplateModalService.showErrorModal(
          event,
          'use_template_cke_error_modal_body',
          false,
          true,
        );
      }
    });
  };

  /**
   * Shows the page snapshot dialog and saves the event in the analytics service.
   */
  function assignCollectionsPage(event) {
    AssignmentService.showAssignPageDialog(that.compositeUnit.id, true);
    const assignPageAnalyticEvent = {
      event: 'AssignPageClick',
    };
    AnalyticsService.pushEvent(assignPageAnalyticEvent);
  };
  /**
   * Copies the current page as a template if the user is authenticated
   * and has the TemplateFeature enabled. Otherwise, shows an upgrade
   * or sign-in modal.
   *
   * @param {Event} event - Triggering event
   * @param {boolean} copyContents - Whether to copy contents with the template
   */
  function copyPageAsTemplate(event) {
    if (BulbAuthService.currentUser.isAuthenticated()) {
      // If the user does not have the TemplateFeature enabled, show premium modal
      if (!BulbAuthService.currentUser.features.TemplateFeature.enabled) {
        AeroDialogService.showPlanUpgradeDialog();
        return;
      }

      that.handleCopyTemplate(event, false);

    } else {
      BulbUseTemplateModalService.useTemplateSigninModal();
    }
  }

  /**
   * Copies contents from page unit if the user is authenticated.
   * Reloads state on success, or shows error/sign-in modal on failure.
   *
   * @param {Object} page - The page unit to copy.
   * @param {boolean} copyContents - Whether to copy the contents as well.
   */
  const copyContents = (page, copyContents) => {
    if (BulbAuthService.currentUser.isAuthenticated()) {
      BulbUnitService.copyPageUnit(page, copyContents)
        .then(() => {
          $state.reload();
        }, (error) => {
          ErrorService.captureAnyError(error, true);
        });
    } else {
      BulbUseTemplateModalService.useTemplateSigninModal();
    }
  };

  /**
   * Copies collection contents as a template if user is authenticated and has access,
   * otherwise shows upgrade or sign-in modal.
   *
   * @param {Event} event - Triggering event
   */
  function copyContentsAsTemplate(event) {
    if (BulbAuthService.currentUser.isAuthenticated()) {
      // If the user does not have the TemplateFeature enabled, show premium modal
      if (!BulbAuthService.currentUser.features.TemplateFeature.enabled) {
        AeroDialogService.showPlanUpgradeDialog();
        return;
      }

      that.handleCopyTemplate(event, true);

    } else {
      BulbUseTemplateModalService.useTemplateSigninModal();
    }
  }

  /**
   * Determines whether to show the tour pulse badge to the user on Create Page button.
   * @returns {boolean|any} true if badge is to be shown.
   */
  this.isCreatePageBadgeToBeShown = function isCreatePageBadgeToBeShown() {
    // Need to get updated prefs to render badge in realtime
    this.currentUserPrefs = BulbAuthService.currentUser.prefs;
    return (!this.currentUserPrefs.onBoarding.onboardingShowMainTour
      && !this.currentUserPrefs.onBoarding.onboardingShowMenuTour
      && this.currentUserPrefs.onBoarding.onboardingShowPageTour);
  };

  /**
   * Update the resume full name.
   */
  this.updateResumeName = function updateResumeName() {
    ResumeService.updateResume(this.resume, $scope.authorCtrl.authorAccount.resumeId).then((resume) => {
      ResumeService.updateResumeName.next(resume.fullName);
    }).catch((error) => {
      BulbErrorService.error(error);
    });
  };

  // Remove the Asset Library Dialog in case it is getting loaded twice
  // to remove the duplicate id in DOM and Adding ARIA-LABEL for AutoSize JS
  let nIncludes = 0;
  $scope.$on('$includeContentRequested', () => {
    nIncludes += 1;
  });

  $scope.$on('$includeContentLoaded', () => {
    nIncludes -= 1;
    if (nIncludes === 0) {
      // page is fully loaded including third party directives &
      // partials
      // Adding dummy ARIA-LABEL since this is not a visible control and not going to be read by narrator
      // for AutoSize JS Markup
      const $autosizeJs = $('#autosizejs');
      $autosizeJs.attr('aria-label', 'dummy-label');
      $autosizeJs.attr('aria-hidden', 'true');
      // Selecting the element with assetLibraryDialog
      const assetLibraryDialogDiv = document.getElementsByClassName('assetLibraryDialog');
      // Condition to check in case assetLibraryDialog is loaded twice in the DOM
      // we are removing from the DOM to get rid of duplicate id error
      if (assetLibraryDialogDiv.length > 1) {
        assetLibraryDialogDiv[0].remove();
      }
    }
  });

  /**
     * Share Controls:
     * These methods and variables are used by share-panel.module components.
      */

  const toggleRecommend = async function toggleRecommend() {
    // Why is this removed here?
    localStorage.removeItem('isAuthorized');
    if (BulbAuthService.currentUser.isAuthenticated()) {
      if (that.compositeUnit.likedByUser) {
        await BulbRecommendService.deleteRecommend(that.compositeUnit).then(() => {
          that.compositeUnit.likedByUser = false;
          that.compositeUnit.likeCount -= 1;
        });
      } else {
        await BulbRecommendService.createRecommend(that.compositeUnit).then(() => {
          that.compositeUnit.likedByUser = true;
          that.compositeUnit.likeCount += 1;
        });
      }
    } else {
      const targetUrl = `/u/${that.compositeUnit.path}`;
      const compositeUnitId = that.compositeUnit.id;

      BulbModalService.open({
        titleCode: '',
        template: '<bulb-recommend-login></bulb-recommend-login>',
        windowClass: 'updated-modal-design bulb-pageview-login-modal',
        closeOnClick: true,
        showOkButton: false,
        showCancelButton: false,
        /* eslint-disable no-param-reassign */
        controller: ['$scope', function modalServiceController($modalScope) {
          $modalScope.targetUrl = targetUrl;
          $modalScope.compositeUnit = compositeUnitId;
        }],
        /* eslint-enable no-param-reassign */
      });
    }
  };

  const getType = function getType() {
    // get unit type
    let type;
    switch (that.compositeUnit['@c']) {
      case '.PageUnitDTO':
        type = 'page';
        break;
      case '.CompositeUnitDTO':
        type = 'collection';
        break;
      case '.UserUnitDTO':
        type = 'profile';
        break;
      case '.GroupDTO':
        type = 'group';
        break;
      default:
        type = 'page';
    }
    return type;
  };

  const createMenuItems = [
    {
      id: 'createPage',
      label: $translate.instant('create_page'),
      icon: 'file',
      command: () => {
        RecordEventService.recordEvent('create_content_page', 'composite_view');
        BulbPageService.createPage(that.compositeUnit.id);
      },
      showAnimatedDot: this.isCreatePageBadgeToBeShown(),
    },
    {
      id: 'createCollection',
      label: $translate.instant('create_collection'),
      icon: 'collection',
      command: () => {
        RecordEventService.recordEvent('create_content_collection', 'composite_view');
        createCollection();
      },
      hidden: !BulbAuthService.currentUser.hasPermission(this.compositeUnit, AUTH.PERMISSIONS.UPDATE),
    },
  ];

  // These MenuItems get updated when pageState changes.
  // This occurs when the user switches between Profile and Resume.
  const profileMenuItems = [
    {
      // eslint-disable-next-line max-len
      label: `${$translate.instant(that.compositeUnit.likedByUser ? 'button_liked' : 'button_like')} ${$translate.instant('this_profile')}`,
      icon: that.compositeUnit.likedByUser ? 'heart-solid' : 'heart',
      command: async () => {
        await toggleRecommend();
        profileMenuItems[0].icon = that.compositeUnit.likedByUser ? 'heart-solid' : 'heart';
        profileMenuItems[0]
          .label = `${$translate.instant(that.compositeUnit.likedByUser ? 'button_liked' : 'button_like')}
            ${$translate.instant('this_profile')}`;
      },
      hidden: BulbAuthService.currentUser.hasPermission(this.compositeUnit, AUTH.PERMISSIONS.UPDATE),
    },
    {
      label: $translate.instant('social_dialog_title'),
      icon: 'paper-plane',
      command: ($event) => BulbShareService.displayShareDialog($event),
      hidden: that.isResumeTabSelected,
    },
    {
      label: $translate.instant('inappropriate_content_modal_title', {
        type: getType(),
      }),
      icon: 'flag',
      command: ($event) => BulbInappropriateContentService.openModal($event, that.compositeUnit),
      // app/menus/menus.d.ts: ColorOptions.ALERT
      color: 'var(--bulb-darker-red-color)',
    },
  ];

  /**
   * Filter out CompositeUnits(collections inside collection).
   * @return All units except composite units.
   */
  function filterCompositeUnits() {
    const { children } = that.compositeUnit;
    if (children.length > 0) {
      return children.filter((unit) => unit.unitType !== 'CompositeUnit');
    }
    return children;
  }

  const optionsMenuItemsAnonymousUser = [
    {
      label: $translate.instant('button_present'),
      icon: 'desktop',
      disabled: filterCompositeUnits().length === 0,
      command: () => {
        const unitToPresent = filterCompositeUnits()[0];
        if (unitToPresent) {
          BulbUnitService.setCurrentUnit(unitToPresent);
          BulbPresentationService.startPresentation(unitToPresent);
        }
      },
    },
    {
      // eslint-disable-next-line max-len
      label: `${$translate.instant(that.compositeUnit.likedByUser ? 'button_liked' : 'button_like')} ${$translate.instant('CompositeUnit')}`,
      icon: that.compositeUnit.likedByUser ? 'heart-solid' : 'heart',
      command: async () => {
        await toggleRecommend();
        optionsMenuItemsAnonymousUser[1].icon = that.compositeUnit.likedByUser ? 'heart-solid' : 'heart';
        optionsMenuItemsAnonymousUser[1]
          .label = `${$translate.instant(that.compositeUnit.likedByUser ? 'button_liked' : 'button_like')}
            ${$translate.instant('CompositeUnit')}`;
      },
    },
    {
      label: $translate.instant('inappropriate_content_modal_title', {
        type: getType(),
      }),
      icon: 'flag',
      command: ($event) => BulbInappropriateContentService.openModal($event, that.compositeUnit),
      hidden: BulbAuthService.currentUser.isAuthenticated(),
      // app/menus/menus.d.ts: ColorOptions.ALERT
      color: 'var(--bulb-darker-red-color)',
    },
  ];

  const optionsMenuItemsLoggedInUser = [
    {
      // $watch function below handles hiding and showing this menu option when templates are toggled
      label: $translate.instant('assignment_triple_dot_label'),
      iconSrc: '../../resources/images/menus/assign_triple_arrow.svg',
      hidden: !BulbAuthService.currentUser.features.AssignmentAccountFeature.assignIsVisible
        || ['EDUCATOR', 'ADMINISTRATOR'].indexOf(BulbAuthService.currentUser.position) === -1
        || !that.canEdit,
      command: ($event) => assignCollectionsPage($event),
      disabled: !this.assignmentCanAssign,
      disabledAction: () => {
        if (!this.assignmentCanAssign) {
          AeroDialogService.showPlanUpgradeDialog();
        }
      },
    },
    {
      label: $translate.instant('pageview_save_as_template'),
      icon: BulbAuthService.currentUser.features.TemplateFeature.enabled ? 'clone' : 'exclamation-square',
      type: BulbAuthService.currentUser.features.TemplateFeature.enabled ? 'TOGGLE' : '',
      model: that.compositeUnit.template,
      descriptiveText: !BulbAuthService.currentUser.features.TemplateFeature.enabled
        ? $translate.instant('pageview_template_upgrade_to_enable') : '',
      hidden: !BulbAuthService.currentUser.hasPermission(that.compositeUnit, AUTH.PERMISSIONS.UPDATE),
      disabled: !BulbAuthService.currentUser.features.TemplateFeature.enabled,
      command: () => {
        // Set the view model data to update the template
        that.compositeUnit.template = !that.compositeUnit.template;
        optionsMenuItemsLoggedInUser[1].model = that.compositeUnit.template;

        SerialSaveService.save({
          save: () => Restangular.one('units', that.compositeUnit.id)
            .customPOST({ template: that.compositeUnit.template }, 'update')
            .then(({ template }) => {
              // Set the view model data with the latest data from the server.
              optionsMenuItemsLoggedInUser[1].model = template;
              optionsMenuItemsLoggedInUser[2].disabled = !template;
            }),
        });
      },
    },
    {
      // $watch function below handles hiding and showing this menu option when templates are toggled
      label: $translate.instant('copy_entire_collection'),
      icon: 'clone',
      command: ($event) => copyPageAsTemplate($event),
    },
    {
      // $watch function below handles hiding and showing this menu option when templates are toggled
      label: $translate.instant('copy_contents_only'),
      icon: 'clone',
      command: () => copyContents(that.compositeUnit, true),
    },
    {
      label: that.compositeUnit.titleHidden
        ? $translate.instant('title_show_text')
        : $translate.instant('title_hide_text'),
      icon: that.compositeUnit.titleHidden ? 'eye' : 'eye-slash',
      command: ($event) => {
        toggleTitleHidden($event);
        optionsMenuItemsLoggedInUser[4].label = that.compositeUnit.titleHidden
          ? $translate.instant('title_show_text')
          : $translate.instant('title_hide_text');
        optionsMenuItemsLoggedInUser[4].icon = that.compositeUnit.titleHidden ? 'eye' : 'eye-slash';
      },
      hidden: !that.canEdit,
    },
    ...optionsMenuItemsAnonymousUser,
    {
      label: $translate.instant('pageview_get_help_text'),
      icon: 'question-circle',
      type: 'LINK',
      link: 'https://help.bulbapp.com/',
      hidden: !(getType() === 'collection'),
    },
    {
      label: $translate.instant('pageview_get_help_text'),
      icon: 'question-circle',
      command: ($event) => {
        BulbModalService.show({
          titleCode: 'pageview_publish_panel_help_title',
          templateUrl: 'collectionhelpmodal.nested',
          showOkButton: false,
          showCancelButton: false,
          focusAfterClosed: $event.currentTarget,
        });
      },
      hidden: getType() === 'collection',
    },
    {
      label: $translate.instant('inappropriate_content_modal_title', {
        type: getType(),
      }),
      icon: 'flag',
      command: ($event) => BulbInappropriateContentService.openModal($event, that.compositeUnit),
      // app/menus/menus.d.ts: ColorOptions.ALERT
      color: 'var(--bulb-darker-red-color)',
    },
    {
      label: `${$translate.instant('button_delete')} ${$translate.instant('CompositeUnit')}`,
      icon: 'trash-alt',
      hidden: !BulbAuthService.currentUser.hasPermission(that.compositeUnit, AUTH.PERMISSIONS.UPDATE),
      command: () => {
        if (that.compositeUnit.isAttachedToResume) {
          deleteResumeCollectionModal();
        } else {
          deleteCollectionModal();
        }
      },
      // app/menus/menus.d.ts: ColorOptions.ALERT
      color: 'var(--bulb-darker-red-color)',
    },
  ];

  $scope.$watch(() => that.compositeUnit.template, (templateValue) => {
    const keysToUpdate = ['copy_entire_collection'];
    keysToUpdate.forEach((key) => {
      const templateCopy = optionsMenuItemsLoggedInUser.find((x) => x.label === $translate.instant(key));
      templateCopy.hidden = !BulbAuthService.currentUser.hasPermission(that.compositeUnit, AUTH.PERMISSIONS.UPDATE)
        || !templateValue;
      templateCopy.disabled = !templateValue;
    });

    const templateCopy = optionsMenuItemsLoggedInUser.find((x) => x.label === $translate.instant('copy_contents_only'));
    templateCopy.hidden = !BulbAuthService.currentUser.hasPermission(that.compositeUnit, AUTH.PERMISSIONS.UPDATE);
  });

  function updateMenuItemLabels() {
    const translation = $translate.instant('inappropriate_content_modal_title', {
      type: that.isResumeTabSelected ? 'resume' : 'profile',
    });
    profileMenuItems[2].label = translation;
    optionsMenuItemsAnonymousUser[2].label = translation;
    optionsMenuItemsLoggedInUser[7].label = translation;
  }

  function updatePageStateForAngular() {
    that.pageState = {
      canEdit: BulbAuthService.currentUser.hasPermission(that.compositeUnit, AUTH.PERMISSIONS.UPDATE),
      isAuthenticated: BulbAuthService.currentUser.isAuthenticated(),
      isResumeTabSelected: that.isResumeTabSelected,
      isTemplate: that.compositeUnit.template,
      type: getType(),
      viewType: getViewType(that.compositeUnit),
      features: {
        PresentationFeature: BulbAuthService.currentUser.features.PresentationFeature,
      },
    };

  }

  that.commandsForAngular = {
    showShareResumeModal: () => showShareResumeModal(),
    showSharePortfolioModal: () => showSharePortfolioModal(),
    copyPageAsTemplate: ($event) => copyPageAsTemplate($event),
    assignCollectionsPage: ($event) => assignCollectionsPage($event),
    copyContentAsTemplate: ($event) => copyContentsAsTemplate($event),
  };

  /**
   * Update the value of showAnimatedDot after the header slide animation is finished and header is fully closed.
   */
  $rootScope.$on('onHeaderFullyClosed', () => {
    that.createMenuItems = createMenuItems.map((item) => {
      if (item.id === 'createPage') {
        return { ...item, showAnimatedDot: this.isCreatePageBadgeToBeShown() };
      }
      return item;
    });
  });

  that.createMenuItems = createMenuItems;
  if (getType() === 'profile') {
    that.optionsMenuItems = profileMenuItems;
  } else {
    that.optionsMenuItems = BulbAuthService.currentUser.isAuthenticated()
      ? optionsMenuItemsLoggedInUser : optionsMenuItemsAnonymousUser;
  }
  updatePageStateForAngular();
}
